import { defineComponent } from 'vue';
import { bindShortcuts } from '@shortcuts/index';

/**
 * Shortcut mixin, expects shortcuts option.
 *
 * @example
 * mixins: [shortcutMixin],
 *
 * shortcuts() {
 *   return {
 *      help: this.help,
 *   }
 * },
 *
 */

export interface ShortcutConfig {
  [key: string]: (event: KeyboardEvent) => void;
}

export const shortcutMixin = defineComponent({
  data(): {
    _shortcutsBound: boolean;
    manualUnbinds: Array<() => void>;
    unbindShortcuts: () => void;
  } {
    return {
      _shortcutsBound: false,
      manualUnbinds: [],
      unbindShortcuts: () => {},
    };
  },

  methods: {
    $bindShortcuts() {
      // avoid double binding
      if (this.$data._shortcutsBound) return;

      // if no shortcuts are defined, don't bind anything
      if (!this.$options.shortcuts) return;

      // Components might have optional shortcuts
      if (!Object.keys(this.$options.shortcuts.call(this)).length) return;

      this.$data._shortcutsBound = true;

      // Wait a tick so that another component that gets destroyed at the same time deregisters
      // its listener before the new one is added
      this.$nextTick(() => {
        this.unbindShortcuts = bindShortcuts(this.$options.shortcuts.call(this));
      });
    },

    $unbindShortcuts() {
      if (!this.$data._shortcutsBound) return;

      this.unbindShortcuts();
      this.$data._shortcutsBound = false;

      // cleanup manually bound shortcuts
      if (this.manualUnbinds) {
        this.manualUnbinds.forEach((unbind) => unbind());
        this.manualUnbinds = [];
      }
    },

    /**
     * Manually bind shortcuts, if a created component handles shortcuts for a popover
     * or something similar that is already created, but not visible.
     *
     * Returns the unbind function.
     *
     * Will be cleaned up, but not rebound.
     */
    $bindShortcutsManually(shortcuts: ShortcutConfig) {
      const unbind = bindShortcuts(shortcuts);

      this.manualUnbinds.push(unbind);

      return unbind;
    },
  },

  /**
   * Don't use mounted here, as child components ae mounted before their parents
   * if multiple components bind to the same shortcut, the bind order must be preserved
   */
  created() {
    this.$bindShortcuts();
  },

  activated() {
    this.$bindShortcuts();
  },

  beforeUnmount() {
    this.$unbindShortcuts();
  },

  deactivated() {
    this.$unbindShortcuts();
  },
});

export default shortcutMixin;
